if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.mediaSwitchRequest = 0;
        this.variantImagePreloads = [];

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        /*
         * PRELOAD THE FIRST IMAGE OF EACH COLOR
         *
         * Runs after the product page has started rendering.
         * Does not preload the whole gallery.
         */
        window.setTimeout(() => {
          this.preloadFirstImageOfEachColor();
        }, 300);

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (!button) return;

            button.addEventListener(
              'click',
              this.setActiveMedia.bind(
                this,
                mediaToSwitch.dataset.target,
                false
              )
            );
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      preloadFirstImageOfEachColor() {
        if (!this.elements.viewer) return;

        const images = [
          ...this.elements.viewer.querySelectorAll('img')
        ];

        if (!images.length) return;

        /*
         * Find the product's Color option.
         */
        const productSection =
          this.closest('section') ||
          this.closest('product-info') ||
          document;

        const fieldsets = [
          ...productSection.querySelectorAll('fieldset')
        ];

        const colorFieldset = fieldsets.find((fieldset) => {
          const legend = fieldset.querySelector('legend');

          return (
            legend &&
            /color|colour/i.test(legend.textContent || '')
          );
        });

        if (!colorFieldset) {
          return;
        }

        /*
         * Read the color list directly from the variant selector,
         * so nothing needs to be hardcoded:
         * Bay, Pepper, Moss...
         *
         * Products with other colors still work.
         */
        const colors = [
          ...new Set(
            [
              ...colorFieldset.querySelectorAll(
                'input[type="radio"]'
              ),
            ]
              .map((input) =>
                String(input.value || '').trim()
              )
              .filter(Boolean)
          ),
        ];

        if (!colors.length) return;

        const normalize = (value) =>
          String(value || '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();

        colors.forEach((color) => {
          const normalizedColor = normalize(color);

          /*
           * The store's current image ALT format:
           * product name + color
           *
           * Example:
           * Too Booked To Care ... Pepper
           */
          const image = images.find((img) => {
            const alt = normalize(img.alt);

            return alt.endsWith(normalizedColor);
          });

          if (!image) return;

          const source =
            image.currentSrc ||
            image.getAttribute('src');

          if (!source && !image.srcset) return;

          /*
           * Create a separate Image object so the browser
           * quietly loads the image into its cache.
           */
          const preload = new Image();

          preload.decoding = 'async';

          /*
           * Auto instead of high:
           * don't compete with the main image currently shown.
           */
          preload.fetchPriority = 'auto';

          if (image.srcset) {
            preload.srcset = image.srcset;

            preload.sizes =
              image.sizes ||
              '(min-width: 750px) 50vw, 100vw';
          }

          if (source) {
            preload.src = source;
          }

          /*
           * Once downloaded, let the browser decode it right away,
           * so clicking a color shows less of a white flash.
           */
          preload.onload = () => {
            if (typeof preload.decode === 'function') {
              preload.decode().catch(() => {});
            }
          };

          /*
           * Keep a reference so the preload object is not
           * garbage-collected too early.
           */
          this.variantImagePreloads.push(preload);
        });
      }

      onSlideChanged(event) {
        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      async setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) return;

        /*
         * Avoid a race when the customer clicks several colors in a row:
         *
         * Blue → Grey → Pepper
         *
         * but Blue finishes loading last and jumps back.
         */
        const requestId =
          ++this.mediaSwitchRequest;

        const image =
          activeMedia.querySelector(
            '.product__modal-opener--image img, img'
          );

        if (image) {
          /*
           * When the customer actively picks a color,
           * this image becomes a high-priority resource.
           */
          image.loading = 'eager';
          image.fetchPriority = 'high';

          image.setAttribute(
            'loading',
            'eager'
          );

          image.setAttribute(
            'fetchpriority',
            'high'
          );

          try {
            /*
             * Thanks to the preload above,
             * this part usually runs almost instantly.
             *
             * The fallback stays for when:
             * - the customer clicks very quickly
             * - the network is slow
             * - the preload has not finished
             */
            if (
              !image.complete ||
              image.naturalWidth === 0
            ) {
              await Promise.race([
                new Promise((resolve) => {
                  image.addEventListener(
                    'load',
                    resolve,
                    {
                      once: true,
                    }
                  );

                  image.addEventListener(
                    'error',
                    resolve,
                    {
                      once: true,
                    }
                  );
                }),

                new Promise((resolve) => {
                  setTimeout(resolve, 1500);
                }),
              ]);
            }

            /*
             * Wait for the browser to decode before swapping the image.
             */
            if (
              typeof image.decode === 'function' &&
              image.complete &&
              image.naturalWidth > 0
            ) {
              await image.decode();
            }
          } catch (error) {
            /*
             * Don't let a decode error break the gallery.
             */
          }
        }

        /*
         * The customer picked another color while waiting
         * → drop the old request.
         */
        if (
          requestId !==
          this.mediaSwitchRequest
        ) {
          return;
        }

        if (prepend) {
          if (
            activeMedia.parentElement
              .firstChild !== activeMedia
          ) {
            activeMedia.parentElement.prepend(
              activeMedia
            );
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement
                .firstChild !== activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        /*
         * Only switch the active state once
         * the new image is ready.
         */
        this.elements.viewer
          .querySelectorAll(
            '[data-media-id]'
          )
          .forEach((element) => {
            element.classList.remove(
              'is-active'
            );
          });

        activeMedia.classList.add(
          'is-active'
        );

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (
            activeMediaRect.top > -0.5
          ) {
            return;
          }

          const top =
            activeMediaRect.top +
            window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(
          activeMedia
        );

        if (
          !this.elements.thumbnails
        ) {
          return;
        }

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        if (!activeThumbnail) return;

        this.setActiveThumbnail(
          activeThumbnail
        );

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset
            .mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute(
              'aria-current'
            )
          );

        const button =
          thumbnail.querySelector(
            'button'
          );

        if (button) {
          button.setAttribute(
            'aria-current',
            true
          );
        }

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo(
          {
            left: thumbnail.offsetLeft,
          }
        );
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (
          !image ||
          !this.elements.liveRegion
        ) {
          return;
        }

        const announce = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings
              .imageAvailable
              .replace(
                '[index]',
                position
              );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        if (
          image.complete &&
          image.naturalWidth > 0
        ) {
          announce();
        } else {
          image.addEventListener(
            'load',
            announce,
            {
              once: true,
            }
          );
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(
            false
          );
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) {
          return;
        }

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (
          !this.elements.viewer.slider
        ) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}